const MIN_CHARS = 2;
const DEBOUNCE_MS = 220;
const LIMIT = 8;
const FETCH_LIMIT = 10;
const LIBRARY_TAG = 'dart lending library';

const isLibraryItem = (product) =>
  (product.tags || []).some((tag) => String(tag).trim().toLowerCase() === LIBRARY_TAG);

const money = (value) => {
  const currency = window.Shopify?.currency?.active || 'USD';
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
};

const esc = (s) =>
  String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );

const cleanUrl = (url) => (url || '').split('?')[0];

function card(product) {
  const img = product.featured_image?.url || product.image || '';
  const varies = product.price_min !== product.price_max;
  const price = varies ? `From ${money(product.price_min)}` : money(product.price);
  const media = img
    ? `<span class="hd-psearch__media"><img src="${esc(img)}" alt="" loading="lazy" width="120" height="120"></span>`
    : '<span class="hd-psearch__media hd-psearch__media--empty"></span>';

  return `<li class="hd-psearch__item">
      <a class="hd-psearch__link" href="${esc(cleanUrl(product.url))}">
        ${media}
        <span class="hd-psearch__text">
          ${product.vendor ? `<span class="hd-psearch__vendor">${esc(product.vendor)}</span>` : ''}
          <span class="hd-psearch__title">${esc(product.title)}</span>
          <span class="hd-psearch__price">${esc(price)}</span>
        </span>
      </a>
    </li>`;
}

function init(root) {
  const input = root.querySelector('[data-hd-search-input]');
  const panel = root.querySelector('[data-hd-search-results]');
  if (!input || !panel) return;

  let timer;
  let controller;
  let lastQuery = '';

  const close = () => {
    panel.hidden = true;
    panel.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
  };

  const open = (html) => {
    panel.innerHTML = html;
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  async function run(query) {
    controller?.abort();
    controller = new AbortController();

    const url =
      `${window.Shopify?.routes?.root || '/'}search/suggest.json` +
      `?q=${encodeURIComponent(query)}` +
      `&resources[type]=product&resources[limit]=${FETCH_LIMIT}`;

    let data;
    try {
      const res = await fetch(url.replace('//search', '/search'), { signal: controller.signal });
      if (!res.ok) throw new Error(String(res.status));
      data = await res.json();
    } catch (err) {
      if (err.name !== 'AbortError') close();
      return;
    }

    if (query !== lastQuery) return;

    const products = (data?.resources?.results?.products || [])
      .filter((product) => !isLibraryItem(product))
      .slice(0, LIMIT);

    if (!products.length) {
      open(
        `<p class="hd-psearch__empty">No products match &ldquo;${esc(query)}&rdquo;.</p>`
      );
      return;
    }

    const all = `/search?q=${encodeURIComponent(query)}&type=product`;
    open(
      `<ul class="hd-psearch__grid" role="list">${products.map(card).join('')}</ul>` +
        `<a class="hd-psearch__all" href="${all}">See all results for &ldquo;${esc(query)}&rdquo;</a>`
    );
  }

  input.addEventListener('input', () => {
    const query = input.value.trim();
    lastQuery = query;
    window.clearTimeout(timer);

    if (query.length < MIN_CHARS) {
      controller?.abort();
      close();
      return;
    }
    timer = window.setTimeout(() => run(query), DEBOUNCE_MS);
  });

  input.addEventListener('focus', () => {
    if (input.value.trim().length >= MIN_CHARS && panel.innerHTML) panel.hidden = false;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !panel.hidden) {
      close();
      input.focus();
    }
  });

  input.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' || panel.hidden) return;
    const first = panel.querySelector('.hd-psearch__link');
    if (first) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Node)) return;
    if (!root.contains(event.target)) close();
  });

  // iOS Safari does not focus a link on tap, so a tap on a suggestion blurs
  // the input with no relatedTarget. Closing right away emptied the panel
  // before the click landed. Remember taps inside the panel, and let the
  // focusout settle before deciding focus really left the search.
  let pressingPanel = false;
  let pressTimer;
  panel.addEventListener('pointerdown', () => {
    pressingPanel = true;
    window.clearTimeout(pressTimer);
    pressTimer = window.setTimeout(() => {
      pressingPanel = false;
    }, 1000);
  });

  root.addEventListener('focusout', (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && root.contains(next)) return;
    window.setTimeout(() => {
      if (pressingPanel) return;
      if (root.contains(document.activeElement)) return;
      close();
    }, 0);
  });
}

document.querySelectorAll('[data-hd-search]').forEach((el) => {
  if (el instanceof HTMLElement) init(el);
});
